import {
    DestinationEvent,
    EventCard,
    FormattedEvent,
    ShareOptionsProps,
    transformDestinationEvent,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { ArrowRightIcon, Button } from '@eventbrite/marmalade';
import React from 'react';
import { Header } from '../../components/Header';
import { Subtitle } from '../../components/Subtitle';
import { ThingsToDoSection } from '../../components/ThingsToDoSection';
import { TRAVEL_AND_OUTDOOR_CATEGORY_SLUG } from '../../constants';
import { useEnvContext, useLocationContext } from '../../context';
import './TopActivities.scss';
import TopActivitiesLoading from './TopActivitiesLoading';

export const THINGS_TO_DO_AFF_CODE = 'ebdssbthingstodo';

const utmOptions = {
    'utm-campaign': 'social',
    'utm-content': 'attendeeshare',
    'utm-medium': 'discovery',
    'utm-term': '',
    'utm-source': '',
    'utm-share-source': 'things-to-do',
};

export interface TopActivitiesProps {
    top_events: DestinationEvent[];
    num_online_events: number;
    num_physical_events: number;
    shareProps?: ShareOptionsProps;
    isLoading?: boolean;
}
export interface TopActivitiesWrapperProps {
    topActivitiesResult: TopActivitiesProps;
    languageSubdirectory?: string;
}

const formatEvents = (events: DestinationEvent[]) => {
    return events.map((event: DestinationEvent) =>
        transformDestinationEvent(event),
    );
};

export const getTitleTexts = (
    num_physical_events: number,
    num_online_events: number,
    shortPlaceName?: string,
) => {
    let title = gettext("%(location)s's top activities", {
        location: shortPlaceName,
    });
    let subtitle = gettext(
        "Your plans, made easy. Check out %(location)s's most popular things to do.",
        { location: shortPlaceName },
    );
    if (num_online_events > 0 && num_physical_events > 0) {
        title = gettext('Top activities in %(location)s and Online', {
            location: shortPlaceName,
        });
        subtitle = gettext(
            'Your plans, made easy. Check out the most popular things to do in %(location)s and online.',
            { location: shortPlaceName },
        );
    } else if (num_online_events > 0 && num_physical_events == 0) {
        title = gettext('Top activities online');
        subtitle = gettext(
            'Your plans, made easy. Check out the most popular things to do online.',
        );
    }
    return {
        title,
        subtitle,
    };
};

const getTravelCategoryBrowseUrl = (
    locationSlug: string,
    languageSubdirectory?: string,
) => {
    return `${
        languageSubdirectory ? `/${languageSubdirectory}` : ''
    }/b/${locationSlug}/${TRAVEL_AND_OUTDOOR_CATEGORY_SLUG}/`;
};

export const TopActivities: React.FunctionComponent<
    TopActivitiesWrapperProps
> = ({ topActivitiesResult, languageSubdirectory }) => {
    const {
        top_events,
        num_online_events,
        num_physical_events,
        shareProps,
        isLoading,
    } = topActivitiesResult;
    const { shortPlaceName, location_slug } = useLocationContext();
    //Get context data
    const { user, env } = useEnvContext();
    const isAuthenticated = !!user?.isAuthenticated;
    const currencyFormat = env?.currencyFormat;
    const formattedEvents = formatEvents(top_events ?? []);
    const titles = getTitleTexts(
        num_physical_events ?? 0,
        num_online_events ?? 0,
        shortPlaceName,
    );
    const bucketLabel = titles.title.toString();

    if (isLoading) {
        return (
            <TopActivitiesLoading
                title={titles.title}
                subtitle={titles.subtitle}
            />
        );
    }

    return (
        <>
            {formattedEvents.length > 0 ? (
                <ThingsToDoSection>
                    <Header title={titles.title} />
                    <Subtitle text={titles.subtitle} />
                    <div className="event-card--container">
                        {formattedEvents.map((event: FormattedEvent) => {
                            return (
                                <div
                                    className="event-card"
                                    data-testid="things-to-do-event"
                                    key={event.id}
                                >
                                    <EventCard
                                        bucketLabel={bucketLabel}
                                        key={event.id}
                                        heapLocationString="ThingsToDo - All"
                                        isAuthenticated={isAuthenticated}
                                        currencyFormat={currencyFormat}
                                        shouldConvertToUserTimezone={true}
                                        eventData={event as FormattedEvent}
                                        isTargetBlank={true}
                                        type={'grid'}
                                        isLazyImage={true}
                                        affCode={THINGS_TO_DO_AFF_CODE}
                                        signal={{
                                            type: 'follow',
                                            value:
                                                event.organizerFollowerCount ||
                                                0,
                                        }}
                                        shareOptions={{
                                            ...shareProps,
                                            eventId: event.id,
                                            eventName: event.name,
                                            eventUrl: event.url,
                                            affCode: '',
                                            utmOptions,
                                            isMobile: false,
                                            serverUrl: '',
                                            twitterHandle: 'Eventbrite',
                                            facebookOptions: {
                                                appId: '28218816837',
                                                version: 'v2.8',
                                                locale: 'en_US',
                                            },
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <div className="view-all-button--container">
                        <a
                            className="view-all-button--link"
                            data-testid="travel-category-browse-link"
                            href={getTravelCategoryBrowseUrl(
                                location_slug || '',
                                languageSubdirectory,
                            )}
                        >
                            <Button size="block">
                                <span className="view-all-button--content">
                                    {gettext('View all')}
                                    <ArrowRightIcon
                                        size="xsmall"
                                        className="view-all-button--icon"
                                    />
                                </span>
                            </Button>
                        </a>
                    </div>
                </ThingsToDoSection>
            ) : (
                <></>
            )}
        </>
    );
};
